import React, { useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import makeStyles from '@mui/styles/makeStyles';
import { ArrowBack } from '@mui/icons-material';
import { blueGrey, grey } from '@mui/material/colors';
import { Avatar, Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import { DealActivityLinkType, SmsDirection } from '@trnsact/trnsact-shared-types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { InputField } from 'components/form/rhf/InputField';

import { SendMessageForm, SmsConversationData } from '../../types';
import { dealActivityApi } from '../../api';
import {
  CreateSmsActivityQuery,
  CreateSmsActivityVariables,
  MarkSmsActivitiesReadQuery,
  MarkSmsActivitiesReadVariables,
  SmsConversationQuery,
  SmsConversationVariables,
} from '../../api/types';
import { ConversationMessageBubble } from './ConversationMessageBubble';
import { useSelector } from 'react-redux';
import { useSmsActivityReceived } from 'hooks/useSubscriptions';

interface Props {
  data: SmsConversationData;
  parentType: DealActivityLinkType;
  onBack: VoidFunction;
}

export const SmsConversationCard = ({ data, parentType, onBack }: Props) => {
  const classes = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const currentUserProfile = useSelector((state: any) => state.userProfile);

  const { control, handleSubmit, reset, watch } = useForm<SendMessageForm>({
    defaultValues: { message: '' },
  });

  const messageValue = watch('message');
  const hasConversation = data.conversationSequenceNumber !== null;

  const {
    data: response,
    loading,
    refetch,
  } = useQuery<SmsConversationQuery, SmsConversationVariables>(dealActivityApi.smsConversation, {
    skip: !hasConversation,
    variables: {
      input: {
        vendorOpportunityId: data.voId,
        conversationSequenceNumber: data.conversationSequenceNumber!,
      },
    },
  });

  const [createSmsActivity, { loading: sending }] = useMutation<CreateSmsActivityQuery, CreateSmsActivityVariables>(
    dealActivityApi.createSmsActivity
  );

  const [markSmsActivitiesRead] = useMutation<MarkSmsActivitiesReadQuery, MarkSmsActivitiesReadVariables>(
    dealActivityApi.markSmsActivitiesRead
  );

  const { data: messageEvent } = useSmsActivityReceived(data.voId);

  const messages = response?.messaging?.smsActivities?.list ?? [];

  useEffect(() => {
    if (hasConversation) {
      markSmsActivitiesRead({
        variables: {
          vendorOpportunityId: data.voId,
          conversationSequenceNumber: data.conversationSequenceNumber!,
        },
      });
    }
  }, []);

  useEffect(() => {
    if (messageEvent?.smsActivityReceived && hasConversation) {
      refetch();
    }
  }, [messageEvent]);

  const onSubmit = async ({ message }: SendMessageForm) => {
    await createSmsActivity({
      variables: {
        input: {
          parentId: data.voId,
          parentType,
          ownerPgCreditApplicationId: data.contactId,
          message: message.trim(),
          smsDirection: SmsDirection.Outgoing,
          conversationSequenceNumber: data.conversationSequenceNumber ?? 0,
          recipientPhone: '+1' + (data.contactPhone?.replace(/\D/g, '') ?? ''),
          senderIdentity: currentUserProfile.email,
        },
      },
    });

    reset();

    if (hasConversation) {
      refetch();
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length]);

  return (
    <Box className={classes.root}>
      <Stack direction="row" alignItems="center" gap="0.5rem" sx={{ padding: '0.75rem 0' }}>
        <IconButton size="small" onClick={onBack}>
          <ArrowBack />
        </IconButton>

        <Typography variant="h6" fontWeight="bold">
          Message Details
        </Typography>
      </Stack>

      <Paper sx={{ display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" alignItems="center" gap="0.75rem" sx={{ padding: '12px 24px', bgcolor: blueGrey[50] }}>
          <Avatar className={classes.contactAvatar}>
            <Typography fontWeight={500}>{data.contactInitials}</Typography>
          </Avatar>

          <Stack>
            <Typography variant="subtitle1" fontWeight="bold">
              {data.contactName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {data.contactPhone}
              {data.contactTitle && ` • ${data.contactTitle}`}
            </Typography>
          </Stack>
        </Stack>

        <Box className={classes.messagesContainer}>
          <RenderWithFallback
            isLoading={loading}
            fallBack={
              <Stack height="100%" alignItems="center" justifyContent="center">
                <CircularProgress />
              </Stack>
            }
          >
            <Stack gap="1rem" sx={{ padding: '1rem 0' }}>
              {messages.length === 0 ? (
                <Stack alignItems="center" justifyContent="center" sx={{ padding: '2rem 0' }}>
                  <Typography variant="subtitle2" component="span">
                    No messages yet
                  </Typography>
                </Stack>
              ) : (
                messages.map(message => <ConversationMessageBubble key={message.id} message={message} />)
              )}
              <Box ref={messagesEndRef} />
            </Stack>
          </RenderWithFallback>
        </Box>

        <Stack
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          gap="0.75rem"
          sx={{ padding: '16px 24px', borderTop: `1px solid ${grey[300]}` }}
        >
          <InputField<SendMessageForm>
            name="message"
            control={control}
            inputProps={{
              multiline: true,
              minRows: 2,
              maxRows: 4,
              placeholder: 'Text Message',
              variant: 'outlined',
              fullWidth: true,
            }}
          />

          <Box>
            <Button type="submit" variant="outlined" color="primary" disabled={!messageValue.trim() || sending}>
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

const useStyles = makeStyles({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  contactAvatar: {
    width: 40,
    height: 40,
    backgroundColor: '#90caf9',
    color: 'black',
    fontSize: '14px',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    minHeight: '200px',
    backgroundColor: '#F7F9F9',
    padding: '0 24px 24px 24px',
  },
});
